import { Navigate, Outlet, Route } from "react-router";

import { queryClient } from "@/providers/QueryClient";
import { loadSearchTablePreferences } from "@/shared/domain/config/loaders/loadSearchTablePreferences";
import { QueryTarget } from "@/shared/domain/query";
import { makeViewsLoader } from "@/shared/domain/views/loaders/loadViews";
import { Goto } from "@/shared/navigation";
import { composeLoaders } from "@/shared/routing/composeLoaders";
import {
  featureFlagRoute,
  makeFeatureFlagRedirectLoader,
} from "@/shared/routing/featureFlagRoute";
import { RedirectToDefaultTab } from "@/shared/routing/RedirectToDefaultTab";

import { CreateSessionPage } from "./CreateSession";
import {
  SessionDetail,
  SessionDetailsTab,
  SessionMetricsTab,
  SessionTab,
  SourcesTab,
  TopicsTab,
} from "./SessionDetail";
import { SessionsPage } from "./SessionsPage";

/**
 * Route tree for `/sessions`.
 *
 * - The list layout (`/sessions`) preloads saved table preferences, then
 *   gates render on the `release-sessions` flag and redirects to
 *   `/search` when off.
 * - The create page (`/sessions/create`) is a sibling of the list layout,
 *   outside its table-preferences loader, which only the list page
 *   consumes. Like the list, it gates on `release-sessions` and redirects
 *   to `/search` when off.
 * - The detail layout (`/sessions/:sessionId/*`) is a sibling rather
 *   than a child of the list — the detail page owns its own chrome.
 *   `featureFlagRoute` returns a single childless `<Route>`, so the
 *   detail gate uses `makeFeatureFlagRedirectLoader` directly on the
 *   parent route here.
 * - The `metrics` tab is gated separately by `release-metrics` — sessions
 *   can ship before metrics dashboards do.
 *
 * Spread into the top-level route list (`{...SessionsRoutes()}`).
 * The inner `featureFlagRoute` calls already bind the absolute paths.
 */
export const SessionsRoutes = () => [
  <Route
    key="sessions-preferences-layout"
    loader={composeLoaders(
      loadSearchTablePreferences(
        queryClient,
        "session_search_table_preferences",
      ),
      // See `DatasetsRoutes`: resolves a `?view=` link before the list mounts.
      makeViewsLoader(queryClient, { target: QueryTarget.Sessions }),
    )}
    element={<Outlet />}
  >
    {featureFlagRoute({
      path: "/sessions",
      flagName: "release-sessions",
      redirectTo: "/search",
      element: <SessionsPage />,
    })}
  </Route>,
  // Sits beside the list layout rather than inside it so navigating here
  // doesn't run the wrapper route's `loadSearchTablePreferences` loader,
  // which only the list page consumes. React Router ranks the static
  // `create` segment above `:sessionId`, so `/sessions/create` never
  // matches the detail route regardless of declaration order.
  featureFlagRoute({
    path: "/sessions/create",
    flagName: "release-sessions",
    redirectTo: "/search",
    element: <CreateSessionPage />,
  }),
  <Route
    key="session-detail"
    path="/sessions/:sessionId"
    loader={makeFeatureFlagRedirectLoader(queryClient, {
      flagName: "release-sessions",
      redirectTo: "/search",
    })}
    element={<SessionDetail />}
  >
    <Route index element={<Navigate to={SessionTab.Topics} replace />} />
    <Route path={SessionTab.Topics} element={<TopicsTab />} />
    <Route path={SessionTab.Sources} element={<SourcesTab />} />
    {featureFlagRoute({
      path: "/sessions/:sessionId/metrics",
      flagName: "release-metrics",
      redirectTo: ({ params }) =>
        `/sessions/${params.sessionId}/${SessionTab.Topics}`,
      element: <SessionMetricsTab />,
    })}
    <Route path={SessionTab.Details} element={<SessionDetailsTab />} />
    <Route
      path="*"
      element={
        <RedirectToDefaultTab
          buildPath={({ sessionId }) =>
            `${Goto.route.Sessions}/${sessionId}/${SessionTab.Topics}`
          }
        />
      }
    />
  </Route>,
];
